/**
 * Every meter in PadLab is a canvas driven by one shared animation frame.
 *
 * Two decisions shape this file.
 *
 * **One loop, not one per meter.** A 4×6 board shows twenty-four pad meters, a stereo
 * master pair and an input meter. Twenty-seven independent `requestAnimationFrame` chains
 * would each wake the compositor on its own schedule and each force its own style
 * resolution; one loop draws them all in a single frame, in one pass, and can be switched
 * off as a unit.
 *
 * **Silence costs nothing.** The loop only runs while something is still changing on its
 * own — a peak-hold marker falling, a clip latch counting down. A board that is sitting
 * idle between takes issues zero frames and the phone's GPU goes back to sleep, which on a
 * device that is also decoding audio is the difference between an hour of battery and
 * three.
 *
 * Meters never ease toward their value. A meter that interpolates is lying about what the
 * audio just did, so every segment is drawn from the number it was handed. The one thing
 * that moves on its own is the peak-hold marker, falling at a fixed 12 dB/s — a
 * measurement convention rather than decoration, which is why Reduce Motion turns it into
 * a static hold instead of removing it.
 */

import { h, onCleanup } from '../../core/dom';
import { clamp } from '../../model/types';
import {
  createCanvasSurface,
  prefersReducedMotion,
  resolveColour,
  roundedRect,
  themeToken,
  withAlpha,
} from './controls';
import type { CanvasSurface } from './controls';

// MARK: - The dB window every meter shares
//
// One scale across the app, so a level looks the same height on the mixer as it does on the
// master strip and a user can compare them at a glance.

const FLOOR_DB = -60;
const CEILING_DB = 0;
const SPAN_DB = CEILING_DB - FLOOR_DB;

const PEAK_FALL_DB_PER_SECOND = 12;
const CLIP_HOLD_MS = 1500;
const CLIP_THRESHOLD = 0.999;
const SILENCE = 0.0005;
/** VoiceOver should hear a level, not a stream of numbers. */
const SPOKEN_INTERVAL_MS = 300;

function linearToDB(linear: number): number {
  return linear <= 0 ? FLOOR_DB : 20 * Math.log10(linear);
}

function decibels(linear: number): number {
  return clamp(linearToDB(clamp(linear, 0, 1)), FLOOR_DB, CEILING_DB);
}

/** 0…1 position of a dB value inside the meter window. */
function normalisedDB(db: number): number {
  return clamp((db - FLOOR_DB) / SPAN_DB, 0, 1);
}

function spokenLevel(linear: number): string {
  const db = decibels(linear);
  if (db <= FLOOR_DB) return 'Silent';
  return `${Math.round(db)} decibels`;
}

// MARK: - The shared clock

interface DrivenMeter {
  /** Advances self-animating state and redraws. Returns true if it needs another frame. */
  frame(now: number): boolean;
}

const driven = new Set<DrivenMeter>();
let frameHandle = 0;

function requestFrame(callback: (now: number) => void): number {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback);
  return self.setTimeout(() => callback(performance.now()), 16);
}

function cancelFrame(handle: number): void {
  if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle);
  else clearTimeout(handle);
}

function tick(now: number): void {
  frameHandle = 0;
  let live = false;
  // Snapshot: a meter torn down from inside its own draw must not corrupt the iteration.
  for (const meter of [...driven]) {
    if (meter.frame(now)) live = true;
  }
  if (live) frameHandle = requestFrame(tick);
}

function wake(): void {
  if (frameHandle === 0 && driven.size > 0) frameHandle = requestFrame(tick);
}

function register(meter: DrivenMeter): () => void {
  driven.add(meter);
  return () => {
    driven.delete(meter);
    if (driven.size === 0 && frameHandle !== 0) {
      cancelFrame(frameHandle);
      frameHandle = 0;
    }
  };
}

/**
 * Whether the shared clock is currently running. Exposed for the performance harness: an
 * idle board must report a stopped clock, and that claim should be testable rather than
 * asserted in a comment.
 */
export function meterClockIsRunning(): boolean {
  return frameHandle !== 0;
}

export interface MeterFrameSubscription {
  /** Ask for a frame after changing state the loop cannot see for itself. */
  wake(): void;
  stop(): void;
}

/**
 * Joins the shared meter clock. `frame` returns true while it still has self-animating
 * state to advance. Used by the spectrum analyser, which has falling peak caps and belongs
 * on the same loop as everything else that visualises the audio.
 */
export function driveMeterFrames(frame: (now: number) => boolean): MeterFrameSubscription {
  const unregister = register({ frame });
  return { wake, stop: unregister };
}

// MARK: - LevelMeter

export type MeterOrientation = 'vertical' | 'horizontal';

export interface LevelMeterOptions {
  readonly orientation?: MeterOrientation;
  readonly segments?: number;
  readonly showsClip?: boolean;
  /** Spoken by VoiceOver — "Master", "Left", "Input". */
  readonly title?: string;
  /** Extra class on the wrapper, for sizing from the screen's own stylesheet. */
  readonly class?: string;
}

export interface LevelMeterHandle {
  readonly el: HTMLElement;
  /** Linear 0…1. `peak` falls back to `rms` when the source has no separate peak. */
  setLevel(rms: number, peak?: number): void;
  /** Drops the peak hold and clears the clip latch — used when the engine is reset. */
  reset(): void;
  destroy(): void;
}

export function levelMeter(options: LevelMeterOptions = {}): LevelMeterHandle {
  const orientation = options.orientation ?? 'vertical';
  const segments = Math.max(4, options.segments ?? 24);
  const showsClip = options.showsClip ?? true;

  let currentRMS = 0;
  let currentPeak = 0;
  let heldPeakDB = FLOOR_DB;
  let heldPeakSetAt = 0;
  let clipLatchedAt = 0;
  let dirty = true;
  let lastSpokenAt = 0;

  const surface = createCanvasSurface('pl-meter__canvas', () => {
    dirty = true;
    wake();
  });

  const root = h(
    'div',
    {
      class: [
        'pl-meter',
        `pl-meter--${orientation}`,
        options.class ?? '',
      ],
      role: 'meter',
      'aria-label': options.title ?? 'Level',
      'aria-valuemin': String(FLOOR_DB),
      'aria-valuemax': String(CEILING_DB),
      'aria-valuenow': String(FLOOR_DB),
      'aria-valuetext': 'Silent',
    },
    surface.canvas,
  );

  function decayedPeakDB(now: number): number {
    if (heldPeakDB <= FLOOR_DB) return FLOOR_DB;
    if (prefersReducedMotion()) return heldPeakDB;
    const elapsed = Math.max(0, (now - heldPeakSetAt) / 1000);
    return Math.max(FLOOR_DB, heldPeakDB - elapsed * PEAK_FALL_DB_PER_SECOND);
  }

  function ingest(now: number): void {
    const db = decibels(currentPeak);
    if (prefersReducedMotion()) {
      // No creeping marker: the hold sits where the loudest recent sample put it and clears
      // the moment the signal stops.
      if (currentPeak <= SILENCE) {
        heldPeakDB = FLOOR_DB;
      } else if (db > heldPeakDB) {
        heldPeakDB = db;
        heldPeakSetAt = now;
      }
    } else if (db > decayedPeakDB(now)) {
      heldPeakDB = db;
      heldPeakSetAt = now;
    }
    if (currentPeak >= CLIP_THRESHOLD) clipLatchedAt = now;
  }

  function draw(now: number): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 0 || height <= 0) return;
    context.clearRect(0, 0, width, height);

    const vertical = orientation === 'vertical';
    const thickness = vertical ? width : height;
    const fullExtent = vertical ? height : width;
    if (thickness <= 0 || fullExtent <= 0) return;

    const clipExtent = showsClip ? Math.min(6, fullExtent * 0.08) : 0;
    const clipGap = showsClip ? 2 : 0;
    const barExtent = Math.max(0, fullExtent - clipExtent - clipGap);
    if (barExtent <= 0) return;
    // The clip cap sits at the loud end: top when vertical, trailing when horizontal.
    const barOrigin = vertical ? clipExtent + clipGap : 0;

    const gap = 1;
    const segment = (barExtent - gap * (segments - 1)) / segments;
    if (segment <= 0) return;
    const radius = Math.min(1.5, segment / 2, thickness / 2);

    const levelDB = decibels(currentRMS);
    const peakDB = Math.max(decayedPeakDB(now), decibels(currentPeak));
    const clipping = clipLatchedAt !== 0 && now - clipLatchedAt < CLIP_HOLD_MS;

    const hot = themeToken('--accent-danger', '#ff4d4d');
    const warn = themeToken('--accent-amber', '#f5c518');
    const safe = themeToken('--accent-success', '#2fefb4');

    for (let index = 0; index < segments; index++) {
      const highDB = FLOOR_DB + (SPAN_DB * (index + 1)) / segments;
      const lowDB = FLOOR_DB + (SPAN_DB * index) / segments;
      const lit = levelDB >= lowDB;
      let shade: string;
      if (highDB > -3) shade = lit ? hot : withAlpha(hot, 0.16);
      else if (highDB > -12) shade = lit ? warn : withAlpha(warn, 0.13);
      else shade = lit ? safe : withAlpha(safe, 0.1);

      const offset = index * (segment + gap);
      context.fillStyle = shade;
      if (vertical) {
        roundedRect(context, 0, barOrigin + barExtent - offset - segment, thickness, segment, radius);
      } else {
        roundedRect(context, offset, 0, segment, thickness, radius);
      }
      context.fill();
    }

    if (peakDB > FLOOR_DB) {
      const position = normalisedDB(peakDB) * barExtent;
      const marker = 2;
      context.fillStyle = themeToken('--tone-primary', '#ffffff');
      if (vertical) {
        const y = clamp(barOrigin + barExtent - position, barOrigin, barOrigin + barExtent - marker);
        context.fillRect(0, y, thickness, marker);
      } else {
        const x = clamp(position - marker, 0, barExtent - marker);
        context.fillRect(x, 0, marker, thickness);
      }
    }

    if (showsClip) {
      const record = themeToken('--accent-record', '#ff3355');
      context.fillStyle = clipping ? record : withAlpha(record, 0.14);
      if (vertical) roundedRect(context, 0, 0, thickness, clipExtent, radius);
      else roundedRect(context, width - clipExtent, 0, clipExtent, thickness, radius);
      context.fill();
    }

    if (now - lastSpokenAt >= SPOKEN_INTERVAL_MS) {
      lastSpokenAt = now;
      root.setAttribute('aria-valuenow', String(Math.round(decibels(currentPeak))));
      root.setAttribute('aria-valuetext', spokenLevel(currentPeak));
    }
  }

  const unregister = register({
    frame(now: number): boolean {
      let needsMore = false;

      if (clipLatchedAt !== 0) {
        if (now - clipLatchedAt >= CLIP_HOLD_MS) {
          clipLatchedAt = 0;
          dirty = true;
        } else {
          needsMore = true;
        }
      }

      if (!prefersReducedMotion() && heldPeakDB > FLOOR_DB) {
        if (decayedPeakDB(now) <= FLOOR_DB) {
          heldPeakDB = FLOOR_DB;
        } else {
          needsMore = true;
        }
        dirty = true;
      }

      if (dirty) {
        dirty = false;
        draw(now);
      }
      return needsMore;
    },
  });

  const handle: LevelMeterHandle = {
    el: root,
    setLevel(rms: number, peak?: number): void {
      const nextRMS = clamp(Number.isFinite(rms) ? rms : 0, 0, 1);
      const nextPeak = clamp(Number.isFinite(peak ?? rms) ? (peak ?? rms) : 0, 0, 1);
      const atRest =
        nextRMS <= SILENCE &&
        nextPeak <= SILENCE &&
        heldPeakDB <= FLOOR_DB &&
        clipLatchedAt === 0;
      // A silent engine pushing zeros every frame must not restart the clock.
      if (nextRMS === currentRMS && nextPeak === currentPeak && atRest) return;
      currentRMS = nextRMS;
      currentPeak = nextPeak;
      ingest(performance.now());
      dirty = true;
      wake();
    },
    reset(): void {
      currentRMS = 0;
      currentPeak = 0;
      heldPeakDB = FLOOR_DB;
      clipLatchedAt = 0;
      dirty = true;
      wake();
    },
    destroy(): void {
      unregister();
      surface.dispose();
      root.remove();
    },
  };

  onCleanup(root, () => {
    unregister();
    surface.dispose();
  });

  surface.measure();
  wake();
  return handle;
}

// MARK: - StereoMeter

export interface StereoLevelValue {
  readonly leftRMS: number;
  readonly rightRMS: number;
  readonly leftPeak: number;
  readonly rightPeak: number;
}

export interface StereoMeterOptions {
  readonly orientation?: MeterOrientation;
  readonly segments?: number;
  readonly showsChannelLabels?: boolean;
  readonly title?: string;
  readonly class?: string;
}

export interface StereoMeterHandle {
  readonly el: HTMLElement;
  setLevel(level: StereoLevelValue): void;
  reset(): void;
  destroy(): void;
}

export function stereoMeter(options: StereoMeterOptions = {}): StereoMeterHandle {
  const orientation = options.orientation ?? 'vertical';
  const showsLabels = options.showsChannelLabels ?? true;

  const build = (name: string): { wrap: HTMLElement; meter: LevelMeterHandle } => {
    const meter = levelMeter({
      orientation,
      ...(options.segments === undefined ? {} : { segments: options.segments }),
      title: name,
    });
    // The group speaks for both channels; two more elements per meter would double the
    // swipe count on the mixer for information the group already gives.
    meter.el.setAttribute('aria-hidden', 'true');
    meter.el.removeAttribute('role');
    const label = showsLabels ? h('span', { class: 'kicker pl-meter__channel' }, name) : null;
    const wrap = h(
      'div',
      { class: 'pl-stereo__channel' },
      orientation === 'vertical' ? meter.el : label,
      orientation === 'vertical' ? label : meter.el,
    );
    return { wrap, meter };
  };

  const left = build('L');
  const right = build('R');

  const root = h(
    'div',
    {
      class: ['pl-stereo', `pl-stereo--${orientation}`, options.class ?? ''],
      role: 'meter',
      'aria-label': options.title ?? 'Output level',
      'aria-valuemin': String(FLOOR_DB),
      'aria-valuemax': String(CEILING_DB),
      'aria-valuenow': String(FLOOR_DB),
      'aria-valuetext': 'Silent',
    },
    left.wrap,
    right.wrap,
  );

  let lastSpokenAt = 0;

  return {
    el: root,
    setLevel(level: StereoLevelValue): void {
      left.meter.setLevel(level.leftRMS, level.leftPeak);
      right.meter.setLevel(level.rightRMS, level.rightPeak);
      const now = performance.now();
      if (now - lastSpokenAt < SPOKEN_INTERVAL_MS) return;
      lastSpokenAt = now;
      const loudest = Math.max(level.leftPeak, level.rightPeak);
      root.setAttribute('aria-valuenow', String(Math.round(decibels(loudest))));
      root.setAttribute(
        'aria-valuetext',
        `Left ${spokenLevel(level.leftPeak)}, right ${spokenLevel(level.rightPeak)}`,
      );
    },
    reset(): void {
      left.meter.reset();
      right.meter.reset();
    },
    destroy(): void {
      left.meter.destroy();
      right.meter.destroy();
      root.remove();
    },
  };
}

// MARK: - PadMeter

/** Pad meters use a tighter floor: a pad audible at all must show travel on a 2 px bar. */
const PAD_FLOOR_DB = -48;
/** -3 dBFS — where one pad starts eating the master's headroom. */
const PAD_HOT_FRACTION = (-3 - PAD_FLOOR_DB) / -PAD_FLOOR_DB;

export interface PadMeterOptions {
  /** Any CSS colour, including `var(--accent-magenta)`. */
  readonly tint?: string;
  readonly width?: number;
  readonly class?: string;
}

export interface PadMeterHandle {
  readonly el: HTMLCanvasElement;
  setLevel(level: number): void;
  setTint(tint: string): void;
  destroy(): void;
}

/**
 * The hairline of level on the trailing edge of a pad tile. Deliberately not segmented: at
 * two pixels wide the gaps would read as noise rather than as a scale.
 *
 * Hidden from assistive technology — the tile already speaks its name and state, and a
 * second element per pad would triple the swipe count on a 4×6 board for no new fact.
 */
export function padMeter(options: PadMeterOptions = {}): PadMeterHandle {
  let tint = resolveColour(options.tint, '#ec5cff');
  let level = 0;
  let dirty = true;

  const surface: CanvasSurface = createCanvasSurface('pl-padmeter', () => {
    dirty = true;
    wake();
  });
  surface.canvas.classList.add(...(options.class === undefined ? [] : [options.class]));
  surface.canvas.style.setProperty('width', `${options.width ?? 2}px`);

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 0 || height <= 0) return;
    context.clearRect(0, 0, width, height);

    // The empty track is structure, so it is drawn in the same grey as every rule in the app.
    // Painting it with the pad's own tint put a soft coloured bar down the trailing edge of
    // all twenty-four tiles at once, at rest — a per-pad accent stripe, which is the one thing
    // the board must not have. Only the level itself is allowed the colour.
    const radius = width / 2;
    context.fillStyle = themeToken('--hairline', '#24242c');
    roundedRect(context, 0, 0, width, height, radius);
    context.fill();

    if (level <= SILENCE) return;
    const db = Math.max(PAD_FLOOR_DB, linearToDB(level));
    const fraction = (db - PAD_FLOOR_DB) / -PAD_FLOOR_DB;
    const filled = Math.max(width, height * fraction);
    context.fillStyle =
      fraction >= PAD_HOT_FRACTION ? themeToken('--accent-danger', '#ff4d4d') : tint;
    roundedRect(context, 0, height - filled, width, filled, radius);
    context.fill();
  }

  const unregister = register({
    frame(): boolean {
      if (dirty) {
        dirty = false;
        draw();
      }
      // A pad meter has no self-animating state: it is only ever as current as its last
      // `setLevel`, so it never asks the clock to keep running.
      return false;
    },
  });

  onCleanup(surface.canvas, () => {
    unregister();
    surface.dispose();
  });

  surface.measure();
  wake();

  return {
    el: surface.canvas,
    setLevel(next: number): void {
      const clamped = clamp(Number.isFinite(next) ? next : 0, 0, 1);
      if (clamped === level) return;
      level = clamped;
      dirty = true;
      wake();
    },
    setTint(next: string): void {
      const resolved = resolveColour(next, '#ec5cff');
      if (resolved === tint) return;
      tint = resolved;
      dirty = true;
      wake();
    },
    destroy(): void {
      unregister();
      surface.dispose();
      surface.canvas.remove();
    },
  };
}

// MARK: - GainReductionMeter

export interface GainReductionMeterOptions {
  readonly maxReductionDB?: number;
  readonly orientation?: MeterOrientation;
  readonly title?: string;
  readonly class?: string;
}

export interface GainReductionMeterHandle {
  readonly el: HTMLElement;
  /** Linear gain the processor is applying, 0…1. 1 means untouched. */
  setGain(gain: number): void;
  destroy(): void;
}

/**
 * How hard the master limiter is working. Fills *away* from the no-reduction end, the way
 * every hardware GR meter reads: the bar grows as the sound is squeezed.
 */
export function gainReductionMeter(
  options: GainReductionMeterOptions = {},
): GainReductionMeterHandle {
  const maxReductionDB = options.maxReductionDB ?? 24;
  const orientation = options.orientation ?? 'horizontal';
  let reductionDB = 0;
  let dirty = true;

  const surface = createCanvasSurface('pl-gr__canvas', () => {
    dirty = true;
    wake();
  });

  const root = h(
    'div',
    {
      class: ['pl-gr', `pl-gr--${orientation}`, options.class ?? ''],
      role: 'meter',
      'aria-label': options.title ?? 'Gain reduction',
      'aria-valuemin': '0',
      'aria-valuemax': String(maxReductionDB),
      'aria-valuenow': '0',
      'aria-valuetext': 'None',
    },
    surface.canvas,
  );

  function draw(): void {
    const context = surface.context;
    if (typeof context.clearRect !== 'function') return;
    const width = surface.width;
    const height = surface.height;
    if (width <= 0 || height <= 0) return;
    context.clearRect(0, 0, width, height);

    const horizontal = orientation === 'horizontal';
    const extent = horizontal ? width : height;
    const thickness = horizontal ? height : width;
    const radius = Math.min(2, thickness / 2);
    const amber = themeToken('--accent-amber', '#f5c518');

    context.fillStyle = withAlpha(amber, 0.1);
    roundedRect(context, 0, 0, width, height, radius);
    context.fill();

    context.fillStyle = themeToken('--hairline-strong', '#33333e');
    for (let tickDB = 6; tickDB < maxReductionDB; tickDB += 6) {
      const position = (tickDB / maxReductionDB) * extent;
      if (horizontal) context.fillRect(width - position, 0, 1, height);
      else context.fillRect(0, position, width, 1);
    }

    if (reductionDB <= 0.05) return;
    const filled = (reductionDB / maxReductionDB) * extent;
    context.fillStyle = amber;
    if (horizontal) roundedRect(context, width - filled, 0, filled, height, radius);
    else roundedRect(context, 0, 0, width, filled, radius);
    context.fill();
  }

  const unregister = register({
    frame(): boolean {
      if (dirty) {
        dirty = false;
        draw();
      }
      return false;
    },
  });

  onCleanup(root, () => {
    unregister();
    surface.dispose();
  });

  surface.measure();
  wake();

  return {
    el: root,
    setGain(gain: number): void {
      const clamped = clamp(Number.isFinite(gain) ? gain : 1, 0, 1);
      const next = clamp(-linearToDB(clamped), 0, maxReductionDB);
      if (Math.abs(next - reductionDB) < 0.05) return;
      reductionDB = next;
      root.setAttribute('aria-valuenow', next.toFixed(1));
      root.setAttribute(
        'aria-valuetext',
        next < 0.1 ? 'None' : `${next.toFixed(1)} decibels of reduction`,
      );
      dirty = true;
      wake();
    },
    destroy(): void {
      unregister();
      surface.dispose();
      root.remove();
    },
  };
}
